import { apiClient } from '@/core/api/client';
import { Endpoints } from '@/core/api/endpoints';
import { logger } from '@/core/logger';

/**
 * Payment fees response from API
 */
export interface PaymentFeesResponse {
    convenienceFee: number; // Percentage (e.g., 0.01 for 1%)
    domesticRate: number; // Percentage (e.g., 0.01 for 1%)
    internationalRate: number; // Percentage (e.g., 0.0125 for 1.25%)
}

/**
 * Checkout split item
 */
export interface CheckoutSplit {
    installment_id: string;
    mid: number;
    revenue_mid: number;
    amount: string;
    payment_type: 'installment' | 'other_charge';
}

/**
 * Create checkout request
 */
export interface CreateCheckoutRequest {
    total_amount: number;
    description: string;
    line_type: 'property_payment';
    splits: CheckoutSplit[];
    tnc_version?: string;
    hmac?: string;
}

/**
 * Create checkout response
 */
export interface CreateCheckoutResponse {
    checkoutUrl: string;
    orderRef: string;
    convenienceFee: number;
    totalAmount: number;
    cardFeeEstimates: {
        domestic: number;
        international: number;
    };
    totalWithCardFee: {
        domestic: number;
        international: number;
    };
}

/**
 * Payment status breakdown item
 */
export interface PaymentStatusBreakdown {
    id: string;
    mid: string;
    value: number;
    payment_type: 'installment' | 'other_charge' | 'revenue';
}

/**
 * Payment status transaction
 */
export interface PaymentStatusTransaction {
    id: string | null;
    authCode: string | null;
    processedAt: string | null;
}

/**
 * Payment status card
 */
export interface PaymentStatusCard {
    type: string | null;
    last4: string | null;
}

/**
 * Payment status request
 */
export interface PaymentStatusRequest {
    orderRef: string;
}

/**
 * Payment error object
 */
export interface PaymentError {
    code: string;
    message: string;
}

/**
 * Payment status response
 */
export interface PaymentStatusResponse {
    cartid: string;
    orderRef: string;
    status: 'PENDING' | 'SUCCESS' | 'FAILED' | 'CANCELLED' | 'DECLINED';
    totalAmount: string;
    paidAmount: string | null;
    currency: string;
    description: string;
    lineType: string;
    breakdown: PaymentStatusBreakdown[];
    transaction: PaymentStatusTransaction;
    card: PaymentStatusCard;
    error: PaymentError | string | null;
    createdAt: string;
    updatedAt: string;
}

/**
 * TNC version response from API
 */
export interface TNCVersionResponse {
    success: boolean;
    data: {
        customer_tnc_version: string;
        payment_tnc_version: string;
    };
}

/**
 * Payment service for payment-related API calls
 */
class PaymentService {
    /**
     * Get payment fees (convenience fee and card processing rates)
     */
    async getPaymentFees(): Promise<PaymentFeesResponse> {
        try {
            logger.debug('PaymentService:Fetching payment fees');
            const response = await apiClient.post<PaymentFeesResponse>(
                Endpoints.payment.fees,
                {}
            );
            logger.debug('PaymentService:Payment fees fetched', response);
            return response;
        } catch (error) {
            logger.error('PaymentService:Failed to fetch payment fees', error);
            throw error;
        }
    }

  /**
   * Create checkout session
   */
  async createCheckout(
    request: CreateCheckoutRequest,
    hmacSignature?: string
  ): Promise<CreateCheckoutResponse> {
    try {
      logger.debug('PaymentService:Creating checkout', request);

      const config =
        hmacSignature && hmacSignature.length > 0
          ? {
              headers: {
                // Backend supports both `hmac` and `x-hmac`, set both for safety
                hmac: hmacSignature,
                "x-hmac": hmacSignature,
              },
            }
          : undefined;

      const response = await apiClient.post<CreateCheckoutResponse>(
        Endpoints.payment.createCheckout,
        request,
        config
      );
      logger.debug('PaymentService:Checkout created', response);
      return response;
    } catch (error) {
      logger.error('PaymentService:Failed to create checkout', error);
      throw error;
    }
  }

    /**
     * Get payment status
     */
    async getPaymentStatus(request: PaymentStatusRequest): Promise<PaymentStatusResponse> {
        try {
            logger.debug('PaymentService:Fetching payment status', request);
            const response = await apiClient.post<PaymentStatusResponse>(
                Endpoints.payment.status,
                request
            );
            logger.debug('PaymentService:Payment status fetched', response);
            return response;
        } catch (error) {
            logger.error('PaymentService:Failed to fetch payment status', error);
            throw error;
        }
    }

    /**
     * Get TNC version from backend
     */
    async getTNCVersion(): Promise<TNCVersionResponse> {
        try {
            logger.debug('PaymentService:Fetching TNC version');
            const response = await apiClient.get<TNCVersionResponse>(
                Endpoints.profile.tnc
            );
            logger.debug('PaymentService:TNC version fetched', response);
            return response;
        } catch (error) {
            logger.error('PaymentService:Failed to fetch TNC version', error);
            throw error;
        }
    }
}

export const paymentService = new PaymentService();

